/**
 * SourceExposureLevelBadge Component
 * Visual badge for displaying source generator exposure levels
 *
 * @version 2025.1.0
 */

import React from 'react';
import { Chip, ChipProps, Tooltip } from '@mui/material';
import {
  Warning as WarningIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import {
  SourceExposureLevel,
  SourceExposureLevelLabels,
  SourceExposureLevelColors,
} from '../../types/sourceApplication';

// ============================================================================
// TYPES
// ============================================================================

export interface SourceExposureLevelBadgeProps {
  /** The exposure level to display */
  level: SourceExposureLevel;
  /** Whether to show the label text */
  showLabel?: boolean;
  /** Size of the badge */
  size?: 'small' | 'medium';
  /** Show days of exposure */
  exposureDays?: number;
  /** Custom tooltip */
  tooltip?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getExposureDescription(level: SourceExposureLevel): string {
  switch (level) {
    case SourceExposureLevel.LOW:
      return 'Exposição ocasional ou em baixa intensidade';
    case SourceExposureLevel.MEDIUM:
      return 'Exposição regular dentro dos limites de tolerância';
    case SourceExposureLevel.HIGH:
      return 'Exposição frequente próximo aos limites de tolerância';
    case SourceExposureLevel.VERY_HIGH:
      return 'Exposição contínua acima dos limites de tolerância';
  }
}

function formatExposureDays(days: number): string {
  if (days === 0) return 'Iniciou hoje';
  if (days === 1) return '1 dia de exposição';
  if (days < 30) return `${days} dias de exposição`;

  const months = Math.floor(days / 30);
  if (months === 1) return '1 mês de exposição';
  if (months < 12) return `${months} meses de exposição`;

  const years = Math.floor(months / 12);
  if (years === 1) return '1 ano de exposição';
  return `${years} anos de exposição`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const SourceExposureLevelBadge: React.FC<SourceExposureLevelBadgeProps> = ({
  level,
  showLabel = true,
  size = 'small',
  exposureDays,
  tooltip,
}) => {
  const color = SourceExposureLevelColors[level];
  const label = SourceExposureLevelLabels[level];
  const isCritical = level === SourceExposureLevel.HIGH || level === SourceExposureLevel.VERY_HIGH;

  const chipContent = showLabel ? label : undefined;
  const chipIcon = isCritical ? (
    level === SourceExposureLevel.VERY_HIGH ? (
      <ErrorIcon fontSize="small" />
    ) : (
      <WarningIcon fontSize="small" />
    )
  ) : undefined;

  const tooltipContent =
    tooltip ||
    (exposureDays !== undefined
      ? `${getExposureDescription(level)} - ${formatExposureDays(exposureDays)}`
      : getExposureDescription(level));

  return (
    <Tooltip title={tooltipContent} arrow>
      <Chip
        label={chipContent}
        icon={chipIcon}
        color={color as ChipProps['color']}
        size={size}
        variant="filled"
        sx={{
          minWidth: showLabel ? 80 : 32,
          fontWeight: isCritical ? 'bold' : 'normal',
        }}
      />
    </Tooltip>
  );
};

export default SourceExposureLevelBadge;
